import React, { useState, useEffect } from 'react';
import { FaArrowUp, FaArrowDown, FaDollarSign, FaBox, FaExclamationCircle, FaSync, FaDownload, FaFilter, FaSearch, FaCalendar, FaCrown } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import EmptyState from '../components/Common/EmptyState';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';
import UpgradePrompt from '../components/Common/UpgradePrompt';
import MetricCard from '../components/Dashboard/MetricCard';

const ProfitTracker = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    profitable: 'all',
    sortBy: 'profit.net_profit',
    sortOrder: 'desc'
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const isPro = user?.subscription?.plan === 'pro';

  useEffect(() => {
    if (isPro) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [filters, isPro]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [productsRes, statsRes] = await Promise.all([
        api.get('/profit-tracker/products', {
          params: { ...filters, limit: 50 }
        }),
        api.get('/profit-tracker/stats')
      ]);

      setProducts(productsRes.data.data || []);
      setStats(statsRes.data.data || null);

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load profit data');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateAll = async () => {
    if (!window.confirm('Calculate profit for all products? This may take several minutes.')) {
      return;
    }

    try {
      setCalculating(true);
      setError('');
      setSuccess('');

      const response = await api.post('/profit-tracker/calculate', {
        all: true,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      setSuccess(`Calculation completed: ${response.data.stats.calculated} products calculated, ${response.data.stats.profitable} profitable`);
      loadData();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate profit');
    } finally {
      setCalculating(false);
    }
  };

  const handleRecalculate = async (asin) => {
    try {
      await api.post(`/profit-tracker/recalculate/${asin}`, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      setSuccess(`Product ${asin} recalculated successfully`);
      loadData();

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to recalculate');
    }
  };

  const exportToExcel = () => {
    const headers = ['ASIN', 'SKU', 'Product', 'Revenue', 'Costs', 'Profit', 'Margin %', 'ROI %', 'Units'];
    const rows = products.map(p => [
      p.asin,
      p.sku,
      p.product_id?.title || 'N/A',
      p.revenue.net_sales.toFixed(2),
      p.costs.total.toFixed(2),
      p.profit.net_profit.toFixed(2),
      p.profit.margin_percentage.toFixed(2),
      p.profit.roi_percentage.toFixed(2),
      p.metrics.units_sold
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profit-tracker-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  if (!isPro) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Profit Tracker</h1>
          <div className="flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-lg">
            <FaCrown className="mr-2" />
            Pro Feature
          </div>
        </div>
        <UpgradePrompt
          title="Unlock Profit Tracker"
          description="Track REAL profit after all costs including Amazon fees, ads, and shipping"
          currentPlan="starter"
        />
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading profit analysis..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Profit Tracker</h1>
          <p className="text-gray-600 mt-1">REAL profit after all costs</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportToExcel}
            disabled={products.length === 0}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <FaDownload className="mr-2" />
            Export CSV
          </button>
          <button
            onClick={handleCalculateAll}
            disabled={calculating}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <FaSync className={`mr-2 ${calculating ? 'animate-spin' : ''}`} />
            {calculating ? 'Calculating...' : 'Calculate All'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {/* Stats Cards */}
      {stats && stats.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <MetricCard
            title="Total Profit"
            value={formatCurrency(stats.summary.total_profit)}
            icon={<FaDollarSign />}
            color="green"
            subtitle={`${stats.summary.avg_margin.toFixed(2)}% avg margin`}
            trend={stats.summary.total_profit > 0 ? 'up' : 'down'}
          />
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(stats.summary.total_revenue)}
            icon={<FaArrowUp />}
            color="blue"
            subtitle={`${stats.summary.total_units} units sold`}
          />
          <MetricCard
            title="Profitable Products"
            value={stats.summary.profitable_count}
            icon={<FaBox />}
            color="green"
            subtitle={`of ${stats.summary.total_products} products`}
          />
          <MetricCard
            title="Unprofitable"
            value={stats.summary.unprofitable_count}
            icon={<FaExclamationCircle />}
            color="red"
            subtitle="Need optimization"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaSearch className="inline mr-1" />
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="ASIN or SKU"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaFilter className="inline mr-1" />
              Profitability
            </label>
            <select
              value={filters.profitable}
              onChange={(e) => setFilters({ ...filters, profitable: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Products</option>
              <option value="yes">Profitable Only</option>
              <option value="no">Unprofitable Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendar className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendar className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Products Table */}
      {products.length === 0 ? (
        <EmptyState
          icon={<FaExclamationCircle className="text-6xl text-gray-400" />}
          title="No profit analysis available"
          description="Click 'Calculate All' to generate profit analysis for your products"
          actionLabel="Calculate All"
          onAction={handleCalculateAll}
        />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Costs
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profit
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Margin
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {product.product_id?.title || 'No title'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {product.asin} • {product.sku}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(product.revenue.net_sales)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {product.metrics.orders_count} orders
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(product.costs.total)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Ads: {formatCurrency(product.costs.ads_cost)}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className={`text-sm font-bold ${product.profit.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(product.profit.net_profit)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatCurrency(product.profit.profit_per_unit)}/unit
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {product.profit.net_profit >= 0 ? (
                          <FaArrowUp className="text-green-500" />
                        ) : (
                          <FaArrowDown className="text-red-500" />
                        )}
                        <span className={`text-sm font-medium ${product.profit.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.profit.margin_percentage.toFixed(2)}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 text-right">
                        ROI: {product.profit.roi_percentage.toFixed(2)}%
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {product.metrics.units_sold}
                      </p>
                      {product.metrics.units_returned > 0 && (
                        <p className="text-xs text-red-500">
                          -{product.metrics.units_returned} returned
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleRecalculate(product.asin)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Recalculate"
                      >
                        <FaSync />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Profitable */}
      {stats && stats.top_profitable && stats.top_profitable.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <FaCrown className="text-yellow-500 mr-2" />
            Top 5 Most Profitable Products
          </h3>
          <div className="space-y-3">
            {stats.top_profitable.map((product, index) => (
              <div key={product._id} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-green-600">#{index + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {product.product_id?.title || product.asin}
                    </p>
                    <p className="text-xs text-gray-500">{product.asin}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(product.profit.net_profit)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {product.profit.margin_percentage.toFixed(2)}% margin
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitTracker;
